import { useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { portalLanguage } from '../i18n';
import {
  VALIDATE_DISCOUNT_CODE,
  VALIDATE_DISCOUNT_CODE_FOR_GUEST,
} from '../util/gql';
import { graphqlErrorResponse } from '../util/validationErrorHelper';
import Button from './Button';
import TextInput from './TextInput';
import styles from '../sass/components/Invoice.module.scss';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The server's refusals are English; in French or Spanish the ones a
 * customer meets are said our way, anything else as "invalid code".
 */
const SERVER_MESSAGE_KEYS: Record<string, string> = {
  'Invalid discount code': 'discount.invalid',
  'Discount code has expired': 'discount.expired',
  "This code can't be used with this email.": 'discount.notWithEmail',
  'Enter your email above, then apply your code.': 'discount.emailFirst',
};

interface DiscountCodeInputProps {
  discountCode?: string;
  setDiscountCode: (discountCode: string) => void;
  selectedServices: {
    value: number;
    name: string;
    id: number;
    nameKey?: string | null;
    serviceTypeName: string;
  }[];
  carId: number | null;
  userId: number;
  email?: string;
  /**
   * Nobody is logged in: the code is checked by the email typed on the
   * Schedule step (validateDiscountCodeForGuest).
   */
  guest?: boolean;
  isDsp?: boolean;
  isInspection?: boolean;
  onDiscountApplied: (
    discountAmount: number | null,
    discountType: string,
    discountCodeId?: string) => void;
  setErrorMessage: (message: string) => void;
  errorMessage?: string;
}

export default function DiscountCodeInput({
  discountCode,
  setDiscountCode,
  selectedServices,
  carId,
  userId,
  email,
  guest = false,
  isDsp,
  isInspection,
  onDiscountApplied,
  errorMessage,
  setErrorMessage,
}: DiscountCodeInputProps) {
  const { t } = useTranslation();
  const [applyDiscountCode, signedIn] = useLazyQuery(VALIDATE_DISCOUNT_CODE);
  const [applyGuestDiscountCode, asGuest] = useLazyQuery(
    VALIDATE_DISCOUNT_CODE_FOR_GUEST,
  );
  const { data, loading, error } = guest ? asGuest : signedIn;
  const validation = guest
    ? data?.validateDiscountCodeForGuest
    : data?.validateDiscountCode;

  const handleApplyDiscount = () => {
    const code = discountCode?.trim();
    if (!code) {
      setErrorMessage(t('discount.enterValid'));
      return;
    }
    const services = selectedServices.map((service) => ({
      id: service.id,
      name: service.name || '',
      value: Math.round(service.value * 100),
      nameKey: service.nameKey || '',
      serviceTypeName: service.serviceTypeName || '',
    }));
    if (guest) {
      const guestEmail = (email || '').trim();
      if (!EMAIL_PATTERN.test(guestEmail)) {
        setErrorMessage(t('discount.emailFirst'));
        return;
      }
      applyGuestDiscountCode({
        variables: {
          code,
          email: guestEmail,
          services,
          carId,
        },
        fetchPolicy: 'network-only',
      });
      return;
    }
    applyDiscountCode({
      variables: {
        code: discountCode,
        services,
        carId,
        userId,
        email,
        isDsp,
        isInspection,
      },
      fetchPolicy: 'network-only',
    });
  };

  useEffect(() => {
    if (validation?.valid) {
      const {
        discountAmount,
        discountType,
        discountCodeId,
      } = validation;
      onDiscountApplied(discountAmount, discountType, discountCodeId);
      setErrorMessage('');
    } else if (data && !validation?.valid) {
      const message = validation?.message;
      setErrorMessage((portalLanguage() === 'en' && message)
        || t(SERVER_MESSAGE_KEYS[message] || 'discount.invalid'));
    } else if (error) {
      setErrorMessage(
        graphqlErrorResponse(error)?.error === 'RATE_LIMIT_EXCEEDED'
          ? t('discount.tooMany')
          : t('discount.error'),
      );
    }
  }, [data, validation, error, onDiscountApplied, setErrorMessage, t]);

  return (
    <div key="discount-code">
      <span className="label">{t('discount.label')}</span>
      <div className={styles.cardContent}>
        <div className={styles.discountInputWrapper}>
          <TextInput
            value={discountCode}
            onChange={(e) => setDiscountCode(e.currentTarget.value)}
            placeholder={t('discount.placeholder')}
            className={styles.discountInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleApplyDiscount();
              }
            }}
          />
          <Button
            className={styles.applyDiscountButton}
            onClick={handleApplyDiscount}
            inactive={loading}
          >
            {t('discount.apply')}
          </Button>
        </div>
        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
      </div>
    </div>
  );
}
